import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Show, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useBreakpointValue, } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import { useQuery } from "../../custom/useQuery";
import { useSettingsContext } from "../../contexts/SettingsContext";
import APButton from "../../components/APButton";
import NavTopBar from "../../components/NavTopBar";
import { ReactComponent as Back } from "../../assets/icons/arrow-back.svg";
import { Cycle, CYCLE_LIST, Tag, TAGS_LIST } from "../../constants/Scenario";
import ScenarioTabContent from "./ScenarioTabContent";
import APFooter from "../../components/APFooter";
import { useVocabIntl } from "../../hooks/useVocabTranslation";

interface ScenariosScreenProps {
  sdk: any;
  backToIntialStep: () => void;
  onStart: (scenarioId?: string) => void;
}

function ScenarioListScreen({ sdk, backToIntialStep, onStart }: ScenariosScreenProps) {
  const navigate = useNavigate();
  const query = useQuery();
  const { getFormattedString, APFormattedMessage, locale } = useVocabIntl();

  const [selectedTag, setSelectedTag] = useState<Tag>(TAGS_LIST[0]); //tags.value
  const [selectedCycle, setSelectedCycle] = useState<Cycle>(CYCLE_LIST[0]); //[intl.locale as keyof Cycle]

  const isLarge = useBreakpointValue({ xs: false, lg: true }, "xs")

  const { setProfileOpen, setSettingModalOpen, setSettingsTabIndex } = useSettingsContext();

  const handleOpenSettings = () => {
    setSettingModalOpen(true);
  };

  const handleToggleTag = (tag: Tag) => {
    setSelectedTag(tag);
  };

  const handleToggleCycle = (cycle: Cycle) => {
    let tag = TAGS_LIST.filter((tag) => {
      return tag.cycle == cycle;
    })[0];

    setSelectedCycle(cycle);
    setSelectedTag(tag);
  };

  const back = () => {
    const demo = query.get("demo");
    let url = "/game";

    if (demo && demo != "") {
      url += "?demo=" + demo;
    }

    navigate(url, { state: "back" });
    backToIntialStep();
  };

  useEffect(() => {
    const demo = query.get("demo");
    const tag: Tag = TAGS_LIST.find((tag) => tag.value === query.get("value")) || TAGS_LIST[0];
    setSelectedTag(tag);
    setSelectedCycle(tag.cycle);
    if (sdk) {
      if (!sdk.getFirebase().isLoggedIn() && !demo) {
        navigate("/", { replace: true });
      }
    }
  }, [sdk]);

  const defaultIndex = TAGS_LIST.findIndex(tag => tag.value === selectedTag.value);

  return (
    <Flex layerStyle={"pageContainer"}>
      <NavTopBar
        backIcon={<Back style={{ width: "1em", height: "1em" }} />}
        handleBack={back}
        backAriaLabel={getFormattedString('aria-label-back-to-main-menu')}
        backTabIndex={0}
        title={
          <Text fontSize={["1rem", "1.5rem"]} fontWeight={"bold"} whiteSpace={"nowrap"} textAlign={"center"}
                layerStyle={"textGlow"}>
            <APFormattedMessage id={"exercises-per-year"} />
          </Text>}
        openProfile={() => setProfileOpen(true)}
        openSettings={() => handleOpenSettings()}
      />
      <Show below={"lg"}>
        <Box display={"flex"} marginY={1} gap={1} justifyContent={"center"}>
          {CYCLE_LIST.map((cycle) => (
            <APButton key={cycle[locale as keyof Cycle]}
                      size={"md"}
                      onClick={() => handleToggleCycle(cycle)}
                      colorScheme={cycle === selectedCycle ? "primary" : "secondary"}>
              {
                getFormattedString('cycle-label', { number: cycle[locale as keyof Cycle] })
              }
            </APButton>
          ))}
        </Box>
      </Show>
      <Flex maxWidth={"container.md"} flexGrow={1} margin={"auto"} height={"50%"} width={"100%"}>
        <Tabs index={defaultIndex} defaultIndex={defaultIndex} colorScheme={"secondary"} size="md"
              variant="solidEnclosed" width={"100%"} isLazy lazyBehavior={"keepMounted"}>
          <TabList justifyContent={"center"}>
            {TAGS_LIST.map((tag, index) => {
                return (
                  <Tab key={tag.value}
                       onClick={() => handleToggleTag(tag)}
                       tabIndex={0}
                       hidden={!isLarge && tag.cycle !== selectedCycle}
                       aria-label={`${getFormattedString('aria-label-list-primaire')} ${tag.label}`}>
                    {tag.label}
                  </Tab>)
              }
            )}
          </TabList>
          <TabPanels display={"flex"} flexDirection={"column"}>
            {useMemo(() => {
              return TAGS_LIST.map((tag, index) => {
                return (
                  <TabPanel key={`scenario-panel-${tag.label}`} display={"flex"} flexDirection={"column"}
                            maxHeight={"100%"}>
                    <ScenarioTabContent key={`scenario-content-${tag.label}`} sdk={sdk} tag={tag} onStart={onStart} />
                  </TabPanel>
                )
              })
            }, [sdk, onStart])}
          </TabPanels>
        </Tabs>
      </Flex>
      <APFooter />
    </Flex>
  );
}

export default ScenarioListScreen;
